import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { omit } from 'ramda';
import { logEventV2 } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { PriceNavigatorStoreModel } from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import { PlatformCostsService } from '../services/platform-costs.service';
import { DialogRef } from '@gelato-api-ui/sdk/src/lib/shared/dialog/dialog-ref';
import { DIALOG_DATA } from '@gelato-api-ui/sdk/src/lib/shared/dialog/dialog-tokens';
import { DialogSize } from '@gelato-api-ui/sdk/src/lib/shared/dialog/types/dialog-size';
import { PriceNavigatorPaywallService } from '@api-ui-app/src/app/price-navigator/prices/services/price-navigator-paywall.service';

interface PlatformCostsModalContext {
  stores: PriceNavigatorStoreModel[];
  currency: string;
}

@Component({
  selector: 'gd-platform-costs-modal',
  templateUrl: './platform-costs-modal.component.html',
  styleUrls: ['./platform-costs-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatformCostsModalComponent {
  size = DialogSize.Small;
  platformCostsForm: FormGroup = this.formBuilder.group({});
  loading$ = new BehaviorSubject<boolean>(true);
  submitting$ = new BehaviorSubject<boolean>(false);
  actionButtonsDisabled$ = combineLatest([this.loading$, this.submitting$]).pipe(
    map(([loading, submitting]) => loading || submitting),
  );

  constructor(
    private readonly dialogRef: DialogRef,
    @Inject(DIALOG_DATA) private readonly dialogData: PlatformCostsModalContext,
    private readonly formBuilder: FormBuilder,
    private readonly platformCostsService: PlatformCostsService,
    private readonly priceNavigatorPaywallService: PriceNavigatorPaywallService,
  ) {
    this.platformCostsService
      .getPlatformCostsByStores(this.stores, this.currency)
      .pipe(take(1))
      .subscribe(platformCosts => {
        const controls = this.stores.map(store => this.formBuilder.control(platformCosts[store.storeId]));
        this.platformCostsForm.addControl('platformCosts', this.formBuilder.array(controls));

        this.loading$.next(false);
      });
  }

  get platformCosts() {
    return this.platformCostsForm.get('platformCosts') as FormArray;
  }

  get stores(): PriceNavigatorStoreModel[] {
    return this.dialogData.stores;
  }

  get currency(): string {
    return this.dialogData.currency;
  }

  close() {
    this.dialogRef.close(false);
  }

  choose() {
    this.priceNavigatorPaywallService.checkUserSubscription().subscribe(hasUserSubscription => {
      if (hasUserSubscription) {
        const selectedCosts = this.platformCostsForm.get('platformCosts').value;

        const storesPlatformCosts = this.stores.reduce((res, { storeId }, index) => {
          res[storeId] = selectedCosts[index];
          return res;
        }, {});

        this.submitting$.next(true);
        this.platformCostsService
          .setPlatformCosts(storesPlatformCosts, this.currency)
          .pipe(take(1))
          .subscribe(() => {
            this.trackPlatformCostsChosenEvent();
            this.dialogRef.close(true);
            this.submitting$.next(false);
          });
      } else {
        this.close();
      }
    });
  }

  private trackPlatformCostsChosenEvent() {
    logEventV2('Pricing Table Platform Costs Chosen', {
      selectedPlatformCosts: Object.entries(this.platformCostsService.selectedPlatformCosts$.value).map(
        ([storeId, platformCost]) => ({
          ...omit(['selected'], platformCost),
          platformType: this.stores.find(store => store.storeId === storeId).type,
        }),
      ),
      selectedCurrency: this.currency,
    });
  }
}
